import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building2, Plus, Trash2, Users, Mail, Phone, Key, 
  RefreshCw, Copy, CheckCircle, XCircle, Shield, Eye, EyeOff, KeyRound, User
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HotelUserManagement = ({ user, onLogout }) => {
  const [hotelUsers, setHotelUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [selectedPropertyFilter, setSelectedPropertyFilter] = useState('all');
  const [apiAccessData, setApiAccessData] = useState({});
  const [visibleKeys, setVisibleKeys] = useState({});
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    property_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [usersRes, propsRes] = await Promise.all([
        axios.get(`${API}/hotel-users`, { headers }),
        axios.get(`${API}/properties`, { headers })
      ]);
      
      setHotelUsers(usersRes.data);
      const activeProps = propsRes.data.filter(p => p.status !== 'archived');
      setProperties(activeProps);
      
      // Initialize API access data from properties
      const apiData = {};
      activeProps.forEach(prop => {
        apiData[prop.id] = {
          enabled: prop.api_enabled || false,
          api_key: prop.api_key || null
        };
      });
      setApiAccessData(apiData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter hotel users by selected property
  const filteredHotelUsers = selectedPropertyFilter === 'all' 
    ? hotelUsers 
    : hotelUsers.filter(u => u.property_id === selectedPropertyFilter);
  
  // Toggle API access for a property
  const handleToggleApiAccess = async (propertyId, enable) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/properties/${propertyId}/api-access`,
        { enable },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApiAccessData(prev => ({
        ...prev,
        [propertyId]: {
          enabled: response.data.api_enabled,
          api_key: response.data.api_key || null
        }
      }));
      
      toast.success(enable ? 'API access enabled' : 'API access disabled');
    } catch (error) {
      toast.error('Failed to update API access');
    }
  };

  // Regenerate API key
  const handleRegenerateKey = async (propertyId) => {
    setRegeneratingKey(propertyId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/properties/${propertyId}/api-access`,
        { enable: true, regenerate: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApiAccessData(prev => ({
        ...prev,
        [propertyId]: {
          enabled: true,
          api_key: response.data.api_key
        }
      }));
      
      toast.success('API key regenerated');
    } catch (error) {
      toast.error('Failed to regenerate API key');
    } finally {
      setRegeneratingKey(null);
    }
  };

  // Copy API key to clipboard
  const handleCopyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard');
  };

  // Toggle key visibility
  const toggleKeyVisibility = (propertyId) => {
    setVisibleKeys(prev => ({ ...prev, [propertyId]: !prev[propertyId] }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.full_name || !formData.property_id) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/hotel-users`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Hotel user created successfully');
      setNewUserCredentials({
        email: response.data.email,
        password: response.data.temp_password
      });
      fetchData();
      setFormData({ email: '', full_name: '', phone: '', property_id: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this hotel user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hotel-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Hotel user deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // Reset password for hotel user
  const handleResetPassword = async (userId) => {
    setResettingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/hotel-users/${userId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewPassword(response.data.new_password);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Copy credentials to clipboard
  const handleCopyCredentials = (username, password) => {
    const text = `Login: ${username}\nPassword: ${password}`;
    navigator.clipboard.writeText(text);
    toast.success('Credentials copied to clipboard');
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.hotel_name === propertyId);
    return prop?.hotel_name || propertyId;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-cream">
      {/* Header */}
      <div className="glass border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/settings">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <h1 className="text-2xl font-serif text-foreground">Hotel User Management</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-hotel-user-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Hotel User
            </Button>
            <Button variant="outline" size="sm" onClick={onLogout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Property Filter */}
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-4">
            <Label className="font-medium">Filter by Property:</Label>
            <Select value={selectedPropertyFilter} onValueChange={setSelectedPropertyFilter}>
              <SelectTrigger className="w-64" data-testid="property-filter">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                {properties.map(p => (
                  <SelectItem key={p.id} value={p.hotel_name}>
                    {p.hotel_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardDescription>Total Hotel Users</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{filteredHotelUsers.length}</p>
            </CardContent>
          </Card>
          <Card className="glass">
            <CardHeader className="pb-2">
              <CardDescription>Properties with Portal Access</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {new Set(filteredHotelUsers.map(u => u.property_id)).size}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Hotel Users List */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-serif text-foreground mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" /> Hotel Users
            {selectedPropertyFilter !== 'all' && (
              <Badge variant="outline" className="ml-2">{selectedPropertyFilter}</Badge>
            )}
          </h3>
          
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredHotelUsers.length === 0 ? (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {selectedPropertyFilter !== 'all' 
                  ? `No hotel users for ${selectedPropertyFilter}` 
                  : 'No hotel users created yet'}
              </p>
              <p className="text-sm text-muted-foreground">
                Create hotel users to give property owners access to their sales data
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHotelUsers.map(hotelUser => (
                <div 
                  key={hotelUser.user_id} 
                  className="p-4 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{hotelUser.full_name}</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Mail className="w-3 h-3" /> {hotelUser.email}
                        </p>
                        {hotelUser.phone && (
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Phone className="w-3 h-3" /> {hotelUser.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <Badge>{getPropertyName(hotelUser.property_id)}</Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          Created: {new Date(hotelUser.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => handleDeleteUser(hotelUser.user_id)}
                        className="text-red-500 hover:text-red-600 hover:border-red-300"
                        data-testid={`delete-${hotelUser.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Login Credentials Section */}
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Login:</span>
                          <code className="bg-muted px-2 py-0.5 rounded font-mono text-xs">
                            {hotelUser.username || hotelUser.email}
                          </code>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setResetPasswordUser(hotelUser);
                          setNewPassword('');
                        }}
                        data-testid={`reset-password-${hotelUser.user_id}`}
                      >
                        <KeyRound className="w-4 h-4 mr-2" />
                        Reset Password
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* API Access Section */}
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-serif text-foreground mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" /> Enable API Access
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            Enable API access for properties to allow their systems to fetch sales data programmatically.
          </p>

          {properties.filter(p => selectedPropertyFilter === 'all' || p.hotel_name === selectedPropertyFilter).length === 0 ? (
            <p className="text-center py-4 text-muted-foreground">No properties found</p>
          ) : (
            <div className="space-y-4">
              {properties
                .filter(p => selectedPropertyFilter === 'all' || p.hotel_name === selectedPropertyFilter)
                .map(prop => {
                  const apiStatus = apiAccessData[prop.id] || { enabled: false, api_key: null };
                  const isKeyVisible = visibleKeys[prop.id];
                  
                  return (
                    <div 
                      key={prop.id} 
                      className={`p-4 rounded-lg border-2 transition-colors ${
                        apiStatus.enabled 
                          ? 'bg-green-50/50 border-green-200' 
                          : 'bg-muted/30 border-transparent'
                      }`}
                      data-testid={`api-access-${prop.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            apiStatus.enabled ? 'bg-green-100' : 'bg-muted'
                          }`}>
                            {apiStatus.enabled 
                              ? <CheckCircle className="w-5 h-5 text-green-600" />
                              : <XCircle className="w-5 h-5 text-muted-foreground" />
                            }
                          </div>
                          <div>
                            <p className="font-medium">{prop.hotel_name}</p>
                            <p className="text-sm text-muted-foreground">{prop.city}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground">
                            {apiStatus.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <Switch
                            checked={apiStatus.enabled}
                            onCheckedChange={(checked) => handleToggleApiAccess(prop.id, checked)}
                            data-testid={`api-toggle-${prop.id}`}
                          />
                        </div>
                      </div>
                      
                      {/* API Key Display */}
                      {apiStatus.enabled && apiStatus.api_key && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <Label className="text-xs text-muted-foreground mb-2 block">API Key</Label>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white rounded border px-3 py-2 font-mono text-sm">
                              {isKeyVisible 
                                ? apiStatus.api_key 
                                : '••••••••••••••••••••••••'
                              }
                            </div>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => toggleKeyVisibility(prop.id)}
                              title={isKeyVisible ? 'Hide key' : 'Show key'}
                            >
                              {isKeyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleCopyKey(apiStatus.api_key)}
                              title="Copy to clipboard"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleRegenerateKey(prop.id)}
                              disabled={regeneratingKey === prop.id}
                              title="Regenerate key"
                            >
                              <RefreshCw className={`w-4 h-4 ${regeneratingKey === prop.id ? 'animate-spin' : ''}`} />
                            </Button>
                          </div>
                          
                          {/* API Usage Instructions */}
                          <div className="mt-3 p-3 bg-white rounded-lg border text-xs space-y-2">
                            <p className="font-medium text-foreground">How to use:</p>
                            <div className="space-y-1">
                              <p className="text-muted-foreground">
                                <strong>Endpoint:</strong> <code className="bg-muted px-1 rounded">{BACKEND_URL}/api/public/v1/sales</code>
                              </p>
                              <p className="text-muted-foreground">
                                <strong>Method:</strong> GET
                              </p>
                              <p className="text-muted-foreground">
                                <strong>Header:</strong> <code className="bg-muted px-1 rounded">X-API-KEY: {isKeyVisible ? apiStatus.api_key : 'YOUR_API_KEY'}</code>
                              </p>
                              <p className="text-muted-foreground">
                                <strong>Parameters:</strong> <code className="bg-muted px-1 rounded">?date_from=YYYY-MM-DD&date_to=YYYY-MM-DD</code>
                              </p>
                            </div>
                            <div className="pt-2 border-t">
                              <p className="text-muted-foreground font-medium">Sample cURL:</p>
                              <code className="block bg-gray-900 text-green-400 p-2 rounded mt-1 text-[10px] break-all">
                                curl -X GET "{BACKEND_URL}/api/public/v1/sales?date_from=2026-01-01&date_to=2026-01-31" -H "X-API-KEY: {isKeyVisible ? apiStatus.api_key : 'YOUR_API_KEY'}"
                              </code>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="glass rounded-2xl p-6 bg-blue-50/50">
          <h4 className="font-medium text-foreground mb-2">About Hotel Portal & API</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Hotel users can only view sales data for their assigned property</li>
            <li>• They can see room availability, bookings, and their revenue share</li>
            <li>• All access is read-only - no editing or management capabilities</li>
            <li>• Credentials are sent via email upon creation</li>
            <li className="pt-2 border-t mt-2"><strong>API Access:</strong> When enabled, hotels can fetch date-wise sales data (customer name, price, GST) using their API key</li>
          </ul>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Create Hotel User</DialogTitle>
          </DialogHeader>
          
          {newUserCredentials ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 font-medium mb-2">User Created Successfully!</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Share these credentials with the hotel owner:
                </p>
                <div className="space-y-2 bg-white p-3 rounded border">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{newUserCredentials.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Key className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {newUserCredentials.password}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Note: This password will not be shown again
                </p>
              </div>
              <Button 
                className="w-full" 
                onClick={() => {
                  setNewUserCredentials(null);
                  setCreateDialogOpen(false);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="property">Property *</Label>
                <Select 
                  value={formData.property_id}
                  onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                >
                  <SelectTrigger data-testid="property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.hotel_name}>
                        {p.hotel_name} - {p.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Property owner's name"
                  required
                  data-testid="full-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="owner@hotel.com"
                  required
                  data-testid="email-input"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              
              <Button type="submit" className="w-full" data-testid="create-user-submit">
                Create User
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={(open) => !open && setResetPasswordUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          
          {resetPasswordUser && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-medium">{resetPasswordUser.full_name}</p>
                <p className="text-sm text-muted-foreground">{resetPasswordUser.email}</p>
                <p className="text-sm text-muted-foreground">{getPropertyName(resetPasswordUser.property_id)}</p>
              </div>
              
              {newPassword ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 font-medium mb-2">New Password Generated!</p>
                    <div className="bg-white p-3 rounded border space-y-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Login:</span>
                        <code className="font-mono bg-muted px-2 py-0.5 rounded text-sm">
                          {resetPasswordUser.username || resetPasswordUser.email}
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Password:</span>
                        <code className="font-mono bg-muted px-2 py-0.5 rounded text-sm font-bold">
                          {newPassword}
                        </code>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Please share these credentials with the hotel owner
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleCopyCredentials(resetPasswordUser.username || resetPasswordUser.email, newPassword)}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Credentials
                    </Button>
                    <Button 
                      className="flex-1"
                      onClick={() => {
                        setResetPasswordUser(null);
                        setNewPassword('');
                      }}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This will generate a new password for this hotel user. The old password will no longer work.
                  </p>
                  
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setResetPasswordUser(null)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="flex-1"
                      onClick={() => handleResetPassword(resetPasswordUser.user_id)}
                      disabled={resettingPassword}
                    >
                      {resettingPassword ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
                      ) : (
                        <><KeyRound className="w-4 h-4 mr-2" /> Reset Password</>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HotelUserManagement;
